import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, CardContent, LinearProgress, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../api/client';
import { useAuth } from '../auth';
import { FeeStrategyCard } from '../components/FeeStrategyCard';
import { messageFromError } from '../components/ApiError';
import { UserAccessCard } from '../components/UserAccessCard';
import type { ApiResponse } from '../types';

type Branding = { name: string; supportEmail: string; logoUrl?: string; primaryColor: string; defaultCurrency: string };
type SettingsTab = 'branding' | 'team' | 'fees';

export function SettingsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<SettingsTab>('branding');
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors, isSubmitting }, setError } = useForm<Branding>();
  const branding = useQuery({ queryKey: ['branding'], queryFn: async () => (await api.get<ApiResponse<Branding>>('/settings/branding')).data.data, enabled: user?.role === 'ADMIN' });
  useEffect(() => { if (branding.data) reset(branding.data); }, [branding.data, reset]);
  const save = useMutation({ mutationFn: (form: Branding) => api.put('/settings/branding', form), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['branding'] }) });

  if (user?.role !== 'ADMIN') return <Alert severity="warning">Only administrators can change platform settings.</Alert>;
  if (branding.isLoading) return <LinearProgress />;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4">Settings</Typography>
        <Typography color="text.secondary">Business details, team access, and hosted payment settings.</Typography>
      </Box>

      <Card sx={{ maxWidth: 960, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_event, nextTab: SettingsTab) => setTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minHeight: 64, textTransform: 'none', fontWeight: 700 } }}
        >
          <Tab value="branding" label="Branding" />
          <Tab value="team" label="Team access" />
          {user.isSuperAdmin && <Tab value="fees" label="Fee strategy" />}
        </Tabs>

        {tab === 'branding' && (
          <CardContent sx={{ maxWidth: 700 }}>
            <Stack spacing={0.5} mb={3}><Typography variant="h6">Hosted payment page</Typography><Typography variant="body2" color="text.secondary">Control the business details customers see when paying an invoice.</Typography></Stack>
            <Box component="form" onSubmit={handleSubmit(async (form) => { try { await save.mutateAsync(form); } catch (error) { setError('root', { message: messageFromError(error) }); } })}>
              <Stack spacing={2}>
                {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
                <TextField label="Business name" {...register('name', { required: 'Business name is required' })} error={Boolean(errors.name)} helperText={errors.name?.message} />
                <TextField label="Support email" type="email" {...register('supportEmail', { required: 'Support email is required' })} />
                <TextField label="Logo URL" {...register('logoUrl')} />
                <TextField label="Primary color" placeholder="#635bff" {...register('primaryColor', { required: 'A hexadecimal primary color is required', pattern: { value: /^#[0-9A-Fa-f]{6}$/, message: 'Use a six-digit hexadecimal color' } })} error={Boolean(errors.primaryColor)} helperText={errors.primaryColor?.message} />
                <TextField select label="Default currency" SelectProps={{ native: true }} {...register('defaultCurrency')}><option value="USD">USD</option><option value="AUD">AUD</option><option value="GBP">GBP</option><option value="EUR">EUR</option><option value="CAD">CAD</option></TextField>
                <Button type="submit" variant="contained" disabled={isSubmitting || save.isPending} sx={{ alignSelf: 'start' }}>Save branding</Button>
              </Stack>
            </Box>
          </CardContent>
        )}

        {tab === 'team' && <CardContent><UserAccessCard /></CardContent>}
        {tab === 'fees' && user.isSuperAdmin && <CardContent><FeeStrategyCard /></CardContent>}
      </Card>
    </Stack>
  );
}
